/**
 * TextareaAtom.tsx
 *
 * A generic textarea atom wrapping PrimeReact's InputTextarea component
 * with design system styling and consistent API.
 */

import React, { forwardRef, useId } from 'react';
import { InputTextarea, InputTextareaProps } from 'primereact/inputtextarea';
import './TextareaAtom.scss';

export type TextareaVariant = 'default' | 'outlined' | 'filled';
export type TextareaSize = 'small' | 'normal' | 'large';
export type TextareaResize = 'none' | 'vertical' | 'horizontal' | 'both';

export interface TextareaAtomProps extends Omit<InputTextareaProps, 'className' | 'variant'> {
  /** Visual variant of the textarea */
  variant?: TextareaVariant;
  /** Size of the textarea */
  size?: TextareaSize;
  /** Resize behavior */
  resize?: TextareaResize;
  /** Additional CSS class names */
  className?: string;
  /** Label to display above the textarea */
  label?: string;
  /** Helper text to display below the textarea */
  helperText?: string;
  /** Error state */
  error?: boolean;
  /** Error message to display */
  errorMessage?: string;
}

const TextareaAtom = forwardRef<HTMLTextAreaElement, TextareaAtomProps>((props, ref) => {
  const {
    variant = 'default',
    size = 'normal',
    resize = 'vertical',
    className = '',
    label,
    helperText,
    error = false,
    errorMessage,
    ...rest
  } = props;

  // The label, helper text and error message are only announced when they are
  // associated with the control, so the atom owns an id whenever the caller
  // does not supply one.
  const generatedId = useId();
  const textareaId = rest.id ?? generatedId;
  const helperId = `${textareaId}-helper`;
  const errorId = `${textareaId}-error`;
  const showHelper = Boolean(helperText) && !error;
  const showError = error && Boolean(errorMessage);
  const describedBy =
    [
      rest['aria-describedby'],
      showHelper ? helperId : undefined,
      showError ? errorId : undefined,
    ]
      .filter(Boolean)
      .join(' ') || undefined;

  // Build CSS classes
  const classes = [
    'textarea-atom',
    `textarea-atom--variant-${variant}`,
    `textarea-atom--size-${size}`,
    `textarea-atom--resize-${resize}`,
    error ? 'textarea-atom--error' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  return (
    <div className="textarea-atom-wrapper">
      {label && (
        <label className="textarea-atom__label" htmlFor={textareaId}>
          {label}
        </label>
      )}
      <InputTextarea
        ref={ref}
        className={classes}
        {...rest}
        id={textareaId}
        aria-invalid={error || undefined}
        aria-describedby={describedBy}
      />
      {showHelper && (
        <small className="textarea-atom__helper" id={helperId}>
          {helperText}
        </small>
      )}
      {showError && (
        <small className="textarea-atom__error-message" id={errorId}>
          {errorMessage}
        </small>
      )}
    </div>
  );
});

TextareaAtom.displayName = 'TextareaAtom';

export default TextareaAtom;
